import type {
  AssignCustomerManagerResponse,
  ListCustomerManagerCustomersResponse,
  RevokeCustomerManagerResponse,
} from '@nadoutong/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { opsApiRequest } from './client.ts';

const CUSTOMERS_BASE = '/v1/ops/customers';
export const CUSTOMER_OWNERSHIP_ROOT_KEY = ['customer-ownership'] as const;

export interface CustomerOwnershipListScope {
  readonly limit: number;
  readonly offset: number;
  readonly email: string;
  readonly customerManagerOpsUserId?: string;
  /**
   * Admin-only cross-brand filter (epic #1030 E1-S4, #1040). Sales
   * sessions may not choose a brand — ops-api ignores the param and
   * injects the server-side sales-profile brand instead.
   */
  readonly brand?: string;
}

function buildQueryString(query: Record<string, string | number | undefined>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined) {
      continue;
    }
    const stringValue = typeof value === 'number' ? value.toString() : value;
    if (stringValue.length === 0) {
      continue;
    }
    params.set(key, stringValue);
  }
  const s = params.toString();
  return s.length > 0 ? `?${s}` : '';
}

export function customerOwnershipListQueryKey(
  scope: CustomerOwnershipListScope,
): readonly ['customer-ownership', 'customers', CustomerOwnershipListScope] {
  return ['customer-ownership', 'customers', scope] as const;
}

export async function listCustomerOwnershipCustomers(
  scope: CustomerOwnershipListScope,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListCustomerManagerCustomersResponse> {
  const qs = buildQueryString({
    limit: scope.limit,
    offset: scope.offset,
    email: scope.email.trim(),
    customerManagerOpsUserId: scope.customerManagerOpsUserId,
    brand: scope.brand,
  });
  return opsApiRequest<ListCustomerManagerCustomersResponse>(
    `${CUSTOMERS_BASE}${qs}`,
    { signal },
    fetchImpl,
  );
}

export async function assignCustomerManager(
  orgId: string,
  customerManagerOpsUserId: string,
  fetchImpl: typeof fetch,
): Promise<AssignCustomerManagerResponse> {
  return opsApiRequest<AssignCustomerManagerResponse>(
    `${CUSTOMERS_BASE}/assignments/${encodeURIComponent(orgId)}`,
    {
      method: 'PUT',
      body: {
        customer_manager_ops_user_id: customerManagerOpsUserId,
      },
    },
    fetchImpl,
  );
}

export async function revokeCustomerManager(
  orgId: string,
  fetchImpl: typeof fetch,
): Promise<RevokeCustomerManagerResponse> {
  return opsApiRequest<RevokeCustomerManagerResponse>(
    `${CUSTOMERS_BASE}/assignments/${encodeURIComponent(orgId)}`,
    {
      method: 'DELETE',
      body: {},
    },
    fetchImpl,
  );
}

export function useCustomerOwnershipCustomers(
  scope: CustomerOwnershipListScope,
): UseQueryResult<ListCustomerManagerCustomersResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: customerOwnershipListQueryKey(scope),
    queryFn: ({ signal }) => listCustomerOwnershipCustomers(scope, fetchImpl, signal),
  });
}

/** Platform `parsePositiveInt(..., 50, 200)` 400s above this. */
export const QUOTA_CUSTOMERS_PAGE_LIMIT = 200;
export const QUOTA_CUSTOMERS_KEY = ['quota-customers'] as const;

/**
 * Walk `/v1/ops/customers` at the platform max page size until
 * `offset + page.length >= total`. One React Query result for the
 * quota-center typeahead / name cache.
 */
export async function listAllQuotaCustomers(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListCustomerManagerCustomersResponse> {
  const customers: ListCustomerManagerCustomersResponse['customers'] = [];
  let offset = 0;
  let total = Number.POSITIVE_INFINITY;
  while (offset < total) {
    const page = await listCustomerOwnershipCustomers(
      { limit: QUOTA_CUSTOMERS_PAGE_LIMIT, offset, email: '' },
      fetchImpl,
      signal,
    );
    customers.push(...page.customers);
    total = page.total;
    if (offset + page.customers.length >= total) break;
    if (page.customers.length === 0) break;
    offset += QUOTA_CUSTOMERS_PAGE_LIMIT;
  }
  return {
    customers,
    total,
    limit: QUOTA_CUSTOMERS_PAGE_LIMIT,
    offset: 0,
  };
}

export interface QuotaCustomersQueryOptions {
  readonly enabled?: boolean;
  readonly staleTime?: number;
  readonly retry?: boolean;
}

export function useQuotaCustomers(
  options: QuotaCustomersQueryOptions = {},
): UseQueryResult<ListCustomerManagerCustomersResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: QUOTA_CUSTOMERS_KEY,
    queryFn: ({ signal }) => listAllQuotaCustomers(fetchImpl, signal),
    ...(options.enabled === undefined ? {} : { enabled: options.enabled }),
    ...(options.staleTime === undefined ? {} : { staleTime: options.staleTime }),
    ...(options.retry === undefined ? {} : { retry: options.retry }),
  });
}

export interface AssignCustomerManagerVars {
  readonly orgId: string;
  readonly customerManagerOpsUserId: string;
}

export function useAssignCustomerManager(): UseMutationResult<
  AssignCustomerManagerResponse,
  Error,
  AssignCustomerManagerVars
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, customerManagerOpsUserId }) =>
      assignCustomerManager(orgId, customerManagerOpsUserId, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CUSTOMER_OWNERSHIP_ROOT_KEY });
    },
  });
}

export function useRevokeCustomerManager(): UseMutationResult<
  RevokeCustomerManagerResponse,
  Error,
  string
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orgId) => revokeCustomerManager(orgId, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CUSTOMER_OWNERSHIP_ROOT_KEY });
    },
  });
}
